import { useMemo } from 'react';
import { Shield, ShieldCheck, AlertTriangle, Lock, Cookie, FileWarning } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { getFileName } from '@har-insight/parser';
import type { HAREntry } from '@har-insight/parser';
import { cn } from '@/lib/utils';

interface SecurityIssue {
  severity: 'critical' | 'high' | 'medium' | 'low';
  category: string;
  title: string;
  description: string;
  entries?: HAREntry[];
  details?: string[];
  recommendation?: string;
}

// Extended security headers list with modern headers
const SECURITY_HEADERS = [
  // Critical headers
  { name: 'content-security-policy', label: 'Content-Security-Policy', severity: 'critical' as const },
  { name: 'strict-transport-security', label: 'Strict-Transport-Security (HSTS)', severity: 'critical' as const },
  
  // High priority headers
  { name: 'x-content-type-options', label: 'X-Content-Type-Options', severity: 'high' as const },
  { name: 'x-frame-options', label: 'X-Frame-Options', severity: 'high' as const },
  { name: 'referrer-policy', label: 'Referrer-Policy', severity: 'high' as const },
  
  // Medium priority headers
  { name: 'permissions-policy', label: 'Permissions-Policy', severity: 'medium' as const },
  { name: 'cross-origin-embedder-policy', label: 'Cross-Origin-Embedder-Policy', severity: 'medium' as const },
  { name: 'cross-origin-opener-policy', label: 'Cross-Origin-Opener-Policy', severity: 'medium' as const },
  { name: 'cross-origin-resource-policy', label: 'Cross-Origin-Resource-Policy', severity: 'medium' as const },
  
  // Low priority / Legacy headers
  { name: 'x-xss-protection', label: 'X-XSS-Protection', severity: 'low' as const },
];

// Common vulnerable patterns in URLs
const VULNERABLE_PATTERNS = [
  { pattern: /[?&](password|passwd|pwd)=/i, label: 'password', severity: 'critical' as const },
  { pattern: /[?&](token|api_key|apikey|secret|access_token|auth_token)=/i, label: 'token/API key', severity: 'critical' as const },
  { pattern: /[?&](ssn|social_security|sin)=/i, label: 'SSN', severity: 'critical' as const },
  { pattern: /[?&](credit_card|cc_number|card_number|cvv|ccv)=/i, label: 'credit card', severity: 'critical' as const },
  { pattern: /[?&](email|phone|mobile|fax)=/i, label: 'PII', severity: 'medium' as const },
  { pattern: /[?&](session|sessionid|jsessionid|phpsessid)=/i, label: 'session ID', severity: 'high' as const },
];

export function SecurityAudit() {
  const { filteredEntries, selectEntry } = useHARStore();

  const issues = useMemo(() => {
    if (filteredEntries.length === 0) return [];

    const result: SecurityIssue[] = [];

    // Get unique domains and their HTML responses
    const htmlResponses = filteredEntries.filter(e =>
      e.response.content.mimeType?.includes('html') &&
      e.response.status >= 200 &&
      e.response.status < 300
    );

    // Check for missing security headers on HTML responses
    const missingHeaders: Record<string, { entries: HAREntry[]; count: number }> = {};
    SECURITY_HEADERS.forEach(h => { 
      missingHeaders[h.name] = { entries: [], count: 0 }; 
    });

    htmlResponses.forEach(entry => {
      const responseHeaders = (entry.response.headers || []).map(h => h.name.toLowerCase());
      SECURITY_HEADERS.forEach(header => {
        if (!responseHeaders.includes(header.name)) {
          // Only add unique domains to avoid duplicate entries
          const domain = new URL(entry.request.url).hostname;
          const existingDomains = missingHeaders[header.name].entries.map(e => new URL(e.request.url).hostname);
          if (!existingDomains.includes(domain)) {
            missingHeaders[header.name].entries.push(entry);
            missingHeaders[header.name].count++;
          }
        }
      });
    });

    SECURITY_HEADERS.forEach(header => {
      if (missingHeaders[header.name].count > 0) {
        const recommendation = getHeaderRecommendation(header.name);
        result.push({
          severity: header.severity,
          category: 'Headers',
          title: `Missing ${header.label}`,
          description: `${missingHeaders[header.name].count} domain(s) missing this security header.`,
          entries: missingHeaders[header.name].entries.slice(0, 3),
          recommendation,
        });
      }
    });

    // Check for insecure cookies
    const insecureCookies: { entry: HAREntry; issues: string[]; cookieNames: string[] }[] = [];

      filteredEntries.forEach(entry => {
      (entry.response.cookies || []).forEach(cookie => {
        const issues: string[] = [];
        const isHttps = entry.request.url.startsWith('https');

        if (isHttps && !cookie.secure) {
          issues.push('Missing Secure flag');
        }
        if (!cookie.httpOnly) {
          issues.push('Missing HttpOnly flag');
        }
        // Check SameSite attribute - TypeScript doesn't know about it but it exists in HAR spec
        const sameSite = (cookie as unknown as { sameSite?: string }).sameSite;
        if (!sameSite) {
          issues.push('Missing SameSite attribute');
        }

        if (issues.length > 0) {
          insecureCookies.push({ entry, issues, cookieNames: [cookie.name] });
        }
      });
    });

    if (insecureCookies.length > 0) {
      // Group by domain
      const byDomain: Record<string, typeof insecureCookies> = {};
      insecureCookies.forEach(item => {
        const domain = new URL(item.entry.request.url).hostname;
        if (!byDomain[domain]) byDomain[domain] = [];
        byDomain[domain].push(item);
      });

      const totalInsecure = Object.values(byDomain).reduce((sum, arr) => sum + arr.length, 0);
      
      result.push({
        severity: 'high',
        category: 'Cookies',
        title: `${totalInsecure} insecure cookie(s)`,
        description: 'Cookies missing security attributes may be vulnerable to theft, XSS, or CSRF attacks.',
        entries: insecureCookies.slice(0, 3).map(c => c.entry),
        details: insecureCookies.slice(0, 3).map(c => c.issues.join(', ')),
        recommendation: 'Add Secure flag for HTTPS cookies, HttpOnly to prevent XSS access, and SameSite to prevent CSRF.',
      });
    }

    // Check for mixed content (HTTP on HTTPS)
    const httpsEntries = filteredEntries.filter(e => e.request.url.startsWith('https'));
    if (httpsEntries.length > 0) {
      const httpEntries = filteredEntries.filter(e =>
        e.request.url.startsWith('http://') &&
        !e.request.url.startsWith('http://localhost')
      );

      if (httpEntries.length > 0) {
        result.push({
          severity: 'critical',
          category: 'Mixed Content',
          title: `${httpEntries.length} HTTP request(s) on HTTPS page`,
          description: 'Loading resources over HTTP on an HTTPS page creates security vulnerabilities and browser warnings.',
          entries: httpEntries.slice(0, 5),
          recommendation: 'Serve all resources over HTTPS. Update resource URLs or use protocol-relative URLs.',
        });
      }
    }

    // Check for sensitive data in URLs
    const sensitiveUrls: { entry: HAREntry; type: string; severity: 'critical' | 'high' | 'medium' }[] = [];
    
    filteredEntries.forEach(entry => {
      VULNERABLE_PATTERNS.forEach(({ pattern, label, severity }) => {
        if (pattern.test(entry.request.url)) {
          sensitiveUrls.push({ entry, type: label, severity });
        }
      });
    });

    if (sensitiveUrls.length > 0) {
      const critical = sensitiveUrls.filter(s => s.severity === 'critical');
      const high = sensitiveUrls.filter(s => s.severity === 'high');
      const medium = sensitiveUrls.filter(s => s.severity === 'medium');
      
      const severity = critical.length > 0 ? 'critical' : high.length > 0 ? 'high' : 'medium';
      
      result.push({
        severity,
        category: 'Data Exposure',
        title: `Sensitive data in ${sensitiveUrls.length} URL(s)`,
        description: `${critical.length > 0 ? `${critical.length} critical, ` : ''}${high.length > 0 ? `${high.length} high risk, ` : ''}${medium.length} medium risk items. Sensitive data in URLs can be logged, cached, or leaked via referrer headers and browser history.`,
        entries: sensitiveUrls.slice(0, 3).map(s => s.entry),
        details: sensitiveUrls.slice(0, 3).map(s => `Contains ${s.type}`),
        recommendation: 'Move sensitive data to request body or headers. Never include passwords, tokens, or PII in URLs.',
      });
    }

    // Check for CORS misconfigurations
    const corsIssues: { entry: HAREntry; issue: string }[] = [];
    
    filteredEntries.forEach(entry => {
      const acao = entry.response.headers.find(h => h.name.toLowerCase() === 'access-control-allow-origin');
      if (acao) {
        if (acao.value === '*') {
          corsIssues.push({ entry, issue: 'Wildcard CORS' });
        } else if (acao.value && !acao.value.includes('http')) {
          // Check if it's allowing null origin or specific domains that might be risky
          corsIssues.push({ entry, issue: `CORS: ${acao.value.substring(0, 30)}...` });
        }
      }
    });

    if (corsIssues.length > 0) {
      const wildcardCount = corsIssues.filter(c => c.issue === 'Wildcard CORS').length;
      
      result.push({
        severity: 'medium',
        category: 'CORS',
        title: `${corsIssues.length} CORS configuration(s)`,
        description: wildcardCount > 0 
          ? `${wildcardCount} endpoint(s) allow any origin (*), which may expose data to malicious websites.`
          : 'Review CORS configurations to ensure they follow the principle of least privilege.',
        entries: corsIssues.slice(0, 3).map(c => c.entry),
        details: corsIssues.slice(0, 3).map(c => c.issue),
        recommendation: 'Avoid wildcard (*) in Access-Control-Allow-Origin. Specify exact domains or implement proper origin validation.',
      });
    }

    // Sort by severity
    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    return result.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
  }, [filteredEntries]);

  if (issues.length === 0) {
    return (
      <div className="card p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-100 dark:bg-green-500/20">
            <ShieldCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100">No security issues detected</h3>
            <p className="text-xs text-slate-500">All checked security headers and practices look good.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4 h-[450px] flex flex-col">
      <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1">
        <div className="space-y-3">
          {issues.map((issue, index) => (
            <div
              key={index}
              className={cn(
                'p-3 rounded-lg border transition-colors',
                issue.severity === 'critical' && 'bg-red-50 dark:bg-red-500/10 border-red-300 dark:border-red-500/30',
                issue.severity === 'high' && 'bg-orange-50 dark:bg-orange-500/10 border-orange-300 dark:border-orange-500/30',
                issue.severity === 'medium' && 'bg-yellow-50 dark:bg-yellow-500/10 border-yellow-300 dark:border-yellow-500/30',
                issue.severity === 'low' && 'bg-blue-50 dark:bg-blue-500/10 border-blue-300 dark:border-blue-500/30'
              )}
            >
              <div className="flex items-start gap-2">
                <div className={cn(
                  'p-1.5 rounded flex-shrink-0',
                  issue.severity === 'critical' && 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400',
                  issue.severity === 'high' && 'bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400',
                  issue.severity === 'medium' && 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-600 dark:text-yellow-400',
                  issue.severity === 'low' && 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400'
                )}>
                  {issue.category === 'Headers' && <Shield className="w-3.5 h-3.5" />}
                  {issue.category === 'Cookies' && <Cookie className="w-3.5 h-3.5" />}
                  {issue.category === 'Mixed Content' && <Lock className="w-3.5 h-3.5" />}
                  {issue.category === 'Data Exposure' && <AlertTriangle className="w-3.5 h-3.5" />}
                  {issue.category === 'CORS' && <FileWarning className="w-3.5 h-3.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={cn(
                      'text-sm font-bold',
                      issue.severity === 'critical' && 'text-red-700 dark:text-red-400',
                      issue.severity === 'high' && 'text-orange-700 dark:text-orange-400',
                      issue.severity === 'medium' && 'text-yellow-700 dark:text-yellow-400',
                      issue.severity === 'low' && 'text-blue-700 dark:text-blue-400'
                    )}>
                      {issue.title}
                    </h3>
                    <span className={cn(
                      'text-[10px] uppercase font-bold px-1.5 py-0.5 rounded',
                      issue.severity === 'critical' && 'bg-red-200 dark:bg-red-500/30 text-red-700 dark:text-red-300',
                      issue.severity === 'high' && 'bg-orange-200 dark:bg-orange-500/30 text-orange-700 dark:text-orange-300',
                      issue.severity === 'medium' && 'bg-yellow-200 dark:bg-yellow-500/30 text-yellow-700 dark:text-yellow-300',
                      issue.severity === 'low' && 'bg-blue-200 dark:bg-blue-500/30 text-blue-700 dark:text-blue-300'
                    )}>
                      {issue.severity}
                    </span>
                    <span className="text-[10px] uppercase font-bold text-slate-400">{issue.category}</span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 font-medium leading-relaxed">
                    {issue.description}
                  </p>

                  {issue.recommendation && (
                    <div className="mt-2 p-2 bg-white/60 dark:bg-black/10 rounded border border-black/5 dark:border-white/5 text-[11px] text-slate-600 dark:text-slate-400">
                      <span className="font-semibold">Fix: </span>{issue.recommendation}
                    </div>
                  )}

                  {issue.entries && issue.entries.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {issue.entries.map((entry, i) => (
                        <button
                          key={entry._id || i}
                          onClick={() => selectEntry(entry)}
                          className="w-full text-left text-xs p-1.5 rounded bg-white/50 dark:bg-slate-900/30 hover:bg-white dark:hover:bg-slate-900/50 transition-colors flex items-center justify-between gap-2 border border-black/5 dark:border-white/5"
                        >
                          <span className="truncate text-slate-700 dark:text-slate-300 font-medium">
                            {getFileName(entry.request.url)}
                          </span>
                          {issue.details?.[i] && (
                            <span className="flex-shrink-0 text-slate-500 font-bold text-[10px]">
                              {issue.details[i]}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Helper function to get recommendations for missing headers
function getHeaderRecommendation(headerName: string): string {
  const recommendations: Record<string, string> = {
    'content-security-policy': 'Implement a strict CSP to prevent XSS attacks. Start with default-src \'self\' and add required sources.',
    'strict-transport-security': 'Add HSTS header with max-age of at least 31536000 (1 year) and include preload directive.',
    'x-content-type-options': 'Add X-Content-Type-Options: nosniff to prevent MIME type sniffing attacks.',
    'x-frame-options': 'Add X-Frame-Options: DENY or SAMEORIGIN to prevent clickjacking attacks.',
    'referrer-policy': 'Add Referrer-Policy: strict-origin-when-cross-origin to control referrer leakage.',
    'permissions-policy': 'Add Permissions-Policy to disable unused browser features and reduce attack surface.',
    'cross-origin-embedder-policy': 'Add COEP: require-corp to prevent cross-origin resource loading without CORS.',
    'cross-origin-opener-policy': 'Add COOP: same-origin to isolate your window from cross-origin windows.',
    'cross-origin-resource-policy': 'Add CORP: same-origin to prevent cross-origin embedding of resources.',
    'x-xss-protection': 'Modern browsers ignore this header. Focus on implementing a strong Content-Security-Policy instead.',
  };
  
  return recommendations[headerName] || 'Add this security header following best practices.';
}
